import Highcharts from 'highcharts/highstock';

/**
 * Line and area series variants that carry a full OHLC bar per point.
 *
 * Highcharts derives the shape of `series.yData` from the series type's `pointArrayMap`, so a
 * plain `line`/`area` series stores a single scalar per point no matter how much data you hand it.
 * Indicators needing the whole bar - ATR, CCI, Stochastic, PSAR, Ichimoku, Aroon, Supertrend and
 * friends - guard with `!isArray(yVal[0]) || yVal[0].length !== 4` and silently draw nothing. That
 * is why those indicators appear broken over a line chart.
 *
 * These variants render exactly like `line`/`area` but declare the OHLC point array map, so every
 * point carries open/high/low/close while `pointValKey` keeps `close` as the plotted value. An
 * indicator linked to one of them sees the same data it would get from a candlestick series.
 *
 * As a side effect this also corrects the plotted value: feeding `[time, o, h, l, c, v]` rows to a
 * stock `line` series makes Highcharts read element `[1]` as `y`, so line charts were plotting the
 * *open* price. They now plot the close.
 */

export const OHLC_LINE_TYPE = 'ohlcline';
export const OHLC_AREA_TYPE = 'ohlcarea';

/** Display types that need an OHLC-carrying variant, keyed by the value held in chart settings. */
const NORMALIZED_TYPES: Record<string, string> = {
    line: OHLC_LINE_TYPE,
    area: OHLC_AREA_TYPE
};

const ohlcSeriesProto = {
    pointArrayMap: ['open', 'high', 'low', 'close'],
    pointValKey: 'close',
    // Scale the axis to the plotted close values rather than the wider high/low range.
    keysAffectYAxis: ['close']
};

let installed = false;

/** Register the OHLC-carrying line/area series types. Safe to call more than once. */
export function installOhlcSeriesTypes() {
    if (installed) return;
    installed = true;

    Highcharts.seriesType(OHLC_LINE_TYPE, 'line', {}, ohlcSeriesProto);
    Highcharts.seriesType(OHLC_AREA_TYPE, 'area', {}, ohlcSeriesProto);
}

/**
 * Map a display type from chart settings to the series type to actually plot, swapping line/area
 * for their OHLC-carrying equivalents. Other types (candlestick, ohlc, hlc, ...) pass through.
 */
export function toSeriesType(displayType: string): string {
    return NORMALIZED_TYPES[displayType] ?? displayType;
}
